import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Minus,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingFooter } from "@/components/marketing/marketing-footer";
import { MarketingNavbar } from "@/components/marketing/marketing-navbar";
import { PricingCard, resolveTierCta } from "@/features/pricing/pricing-card";
import { pricingTiers } from "@/features/pricing/tiers";
import { useAuthStore } from "@/stores/auth-store";

const comparisonRows: Array<{
  label: string;
  values: [string, string, string, string];
}> = [
  { label: "Kullanıcı sayısı", values: ["1", "5", "15", "Sınırsız"] },
  { label: "Şube sayısı", values: ["1", "2", "10", "Sınırsız"] },
  { label: "Müşteri kaydı", values: ["500", "5.000", "25.000", "Sınırsız"] },
  { label: "Araç kaydı", values: ["750", "10.000", "50.000", "Sınırsız"] },
  {
    label: "Aylık servis kaydı",
    values: ["150", "1.500", "7.500", "Sınırsız"],
  },
  {
    label: "Parça ve tedarik kaydı",
    values: ["250", "2.500", "15.000", "Sınırsız"],
  },
  { label: "Servis geçmişi ve plaka takibi", values: ["✓", "✓", "✓", "✓"] },
  { label: "Çalışan rolleri ve yetkileri", values: ["—", "✓", "✓", "✓"] },
  { label: "Tedarikçi cari hesap takibi", values: ["—", "✓", "✓", "✓"] },
  { label: "Periyodik bakım hatırlatma", values: ["—", "✓", "✓", "✓"] },
  { label: "Detaylı mali rapor", values: ["Temel", "✓", "✓", "✓"] },
  { label: "CSV dışa aktarım", values: ["—", "✓", "✓", "✓"] },
  { label: "Denetim (audit) logu", values: ["—", "—", "✓", "✓"] },
  { label: "API erişimi", values: ["—", "—", "✓", "✓"] },
  { label: "Otomatik günlük yedekleme", values: ["—", "—", "✓", "✓"] },
  { label: "SLA garantisi", values: ["—", "—", "Standart", "Genişletilmiş"] },
  { label: "Özel entegrasyonlar (muhasebe, ERP)", values: ["—", "—", "—", "✓"] },
  { label: "On-premise / özel bulut", values: ["—", "—", "—", "✓"] },
  { label: "Size özel hesap yöneticisi", values: ["—", "—", "—", "✓"] },
  { label: "E-posta destek", values: ["✓", "Öncelikli", "Öncelikli", "7/24"] },
];

const pricingFaqs = [
  {
    q: "İlk 14 gün gerçekten ücretsiz mi? Kredi kartı isteyecek misiniz?",
    a: "Evet. Hesap oluşturduğunuzda kredi kartı bilgisi vermeden 14 gün boyunca dilediğiniz paketi deneyebilirsiniz. Deneme süresi bittiğinde otomatik ücret alınmaz; devam etmek isterseniz paket seçip ödeme yaparsınız.",
  },
  {
    q: "Aylık ödeme dışında yıllık plan var mı? İndirim uygulanıyor mu?",
    a: "Evet. Yıllık peşin ödeme yapan işletmelere iki ay hediye ediyoruz (net %16 indirim). Yıllık ödemeye geçmek için hesabınızın Dükkan Ayarları sayfasından başvurabilir veya bizimle iletişime geçebilirsiniz.",
  },
  {
    q: "Paket değiştirebilir miyim? Yükseltmek/düşürmek nasıl işliyor?",
    a: "Paketinizi istediğiniz zaman değiştirebilirsiniz. Yükseltmede fark ücreti anlık hesaplanır, düşürmede ise mevcut dönem sonuna kadar üst paket özellikleri devam eder, sonraki dönem yeni paket üzerinden ilerler.",
  },
  {
    q: "Aboneliğimi iptal edersem verilerime ne oluyor?",
    a: "Abonelik iptalinde verileriniz 90 gün boyunca güvenle saklanır. Bu süre içinde tekrar aktif olursanız kaldığınız yerden devam edersiniz. 90 gün sonunda veriler dışa aktarma dosyası olarak size iletilir ve sistemden kalıcı olarak silinir.",
  },
  {
    q: "Hangi ödeme yöntemlerini destekliyorsunuz?",
    a: "Banka kartları, kredi kartları (Visa, MasterCard, Amex) ve havale/EFT ile ödeme yapabilirsiniz. Kurumsal paketler için elden fatura ile aylık/yıllık ödeme opsiyonu mevcuttur.",
  },
  {
    q: "Fatura kesiliyor mu? E-Arşiv fatura alabilir miyim?",
    a: "Her ödemenizin ardından e-Arşiv faturanız kayıtlı e-posta adresinize otomatik olarak iletilir. Fatura üzerindeki firma bilgileriniz Dükkan Ayarları sayfasından her zaman güncellenebilir.",
  },
  {
    q: "Ek kullanıcı veya ek şube eklemek istersem ekstra ücret var mı?",
    a: "Paketinizde tanımlı sınırların üzerine çıkmak isterseniz bir üst pakete yükseltmeniz gerekir. İhtiyaca özel ek kullanıcı/şube paketi almak için Kurumsal ve Elit paketlerde bizimle iletişime geçebilirsiniz.",
  },
  {
    q: "Fiyatlara KDV dahil mi?",
    a: "Hayır. Sayfada belirtilen tüm fiyatlar KDV hariçtir. Faturanızda %20 KDV eklenerek yer alır.",
  },
];

export function PricingPage() {
  const isAuthed = useAuthStore((s) => Boolean(s.user));

  return (
    <>
      <title>Fiyatlandırma | Ustorio</title>
      <meta
        name="description"
        content="Ustorio paketleri: Başlangıç 749 ₺, Profesyonel 1.799 ₺, Kurumsal 3.999 ₺ ve özel Elit paketi. Atölyenizin ölçeğine uygun paketi seçin, 14 gün ücretsiz deneyin."
      />
      <link rel="canonical" href="https://ustorio.com.tr/fiyatlandirma" />

      <div className="relative min-h-svh overflow-hidden bg-white text-ink-900">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle,rgba(15,23,42,0.04)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:radial-gradient(ellipse_at_center,black_0%,black_60%,transparent_95%)]"
        />

        <MarketingNavbar isAuthed={isAuthed} />

        <main className="relative z-10">
          <PricingHero />
          <PlansGrid isAuthed={isAuthed} />
          <TrustBanner />
          <ComparisonTable />
          <PricingFaqSection />
          <PricingCta isAuthed={isAuthed} />
        </main>

        <MarketingFooter />

        <PricingJsonLd />
      </div>
    </>
  );
}

/* ------------------------------- Hero -------------------------------------- */

function PricingHero() {
  return (
    <section className="relative px-6 pb-12 pt-16 sm:pt-24 lg:px-12 lg:pb-16 lg:pt-28">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-brand-200/50 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-10 h-72 w-72 rounded-full bg-orange-200/40 blur-3xl"
      />

      <div className="relative mx-auto max-w-3xl text-center">
        <h1 className="text-balance text-4xl font-bold leading-[1.05] tracking-[-0.02em] text-ink-900 sm:text-5xl lg:text-6xl">
          Atölyenin ölçeğine göre{" "}
          <span className="bg-gradient-to-r from-brand-500 via-brand-600 to-brand-800 bg-clip-text text-transparent">
            şeffaf fiyatlandırma.
          </span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-balance text-base leading-relaxed text-ink-600 sm:text-lg">
          Küçük bir atölyeden çok şubeli işletmeye kadar Ustorio sizinle birlikte
          büyür. Gizli ücret yok, taahhüt yok. 14 gün ücretsiz deneyin,
          beğenmezseniz ödemezsiniz.
        </p>

        <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-ink-500">
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-brand-600" />
            14 gün ücretsiz deneme
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-brand-600" />
            Kredi kartı istenmiyor
          </li>
          <li className="flex items-center gap-2">
            <Check className="h-4 w-4 text-brand-600" />
            İstediğiniz zaman iptal
          </li>
        </ul>
      </div>
    </section>
  );
}

/* -------------------------------- Plans grid ------------------------------- */

function PlansGrid({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="px-6 py-12 lg:px-12 lg:py-16">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          {pricingTiers.map((tier) => {
            const cta = resolveTierCta(tier, isAuthed);
            return (
              <PricingCard
                key={tier.key}
                tier={tier}
                href={cta.href}
                label={cta.label}
              />
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-ink-500">
          Fiyatlara KDV dahil değildir. Yıllık planlarda iki ay hediye
          (net %16 indirim).
        </p>
      </div>
    </section>
  );
}

/* ------------------------------- Trust banner ------------------------------ */

function TrustBanner() {
  const items = [
    {
      icon: ShieldCheck,
      title: "Verileriniz güvende",
      body: "TLS şifreli iletim, günlük yedekleme ve tenant izolasyonu ile her atölyenin verisi kendine özel alanda tutulur.",
    },
    {
      icon: Wallet,
      title: "Şeffaf faturalama",
      body: "Aylık veya yıllık ödeme, e-Arşiv fatura, gizli ücret yok. Ne kadar ödeyeceğinizi her zaman bilirsiniz.",
    },
    {
      icon: RefreshCw,
      title: "İstediğiniz zaman değiştirin",
      body: "Yükseltme, düşürme veya iptal tek tıkla. Cezai şart yok, uzun vadeli taahhüt yok.",
    },
  ];
  return (
    <section className="px-6 py-14 lg:px-12 lg:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-6 sm:grid-cols-3">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="rounded-2xl border border-ink-200/70 bg-white p-6"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-ink-900 sm:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">
                  {item.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- Comparison table ----------------------------- */

function ComparisonTable() {
  const tierNames: [string, string, string, string] = [
    "Başlangıç",
    "Profesyonel",
    "Kurumsal",
    "Elit",
  ];

  const renderCell = (value: string, isHighlight: boolean) => {
    if (value === "✓") {
      return (
        <Check
          className={
            "mx-auto h-5 w-5 " + (isHighlight ? "text-brand-600" : "text-ink-700")
          }
        />
      );
    }
    if (value === "—") {
      return <Minus className="mx-auto h-4 w-4 text-ink-300" />;
    }
    return (
      <span
        className={
          "text-sm " +
          (isHighlight ? "font-semibold text-brand-700" : "text-ink-800")
        }
      >
        {value}
      </span>
    );
  };

  return (
    <section className="px-6 py-14 lg:px-12 lg:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
            Karşılaştırma
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl">
            Paketlerin ayrıntılı karşılaştırması
          </h2>
          <p className="mt-3 text-base leading-relaxed text-ink-600">
            Hangi paket size uygun? Tüm özellikleri yan yana görün.
          </p>
        </div>

        <div className="mt-10 overflow-x-auto rounded-2xl border border-ink-200/70 bg-white">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead className="bg-ink-50/60">
              <tr>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Özellik
                </th>
                {tierNames.map((name, i) => {
                  const isHighlight = i === 1;
                  return (
                    <th
                      key={name}
                      className={
                        "px-5 py-4 text-center text-xs font-semibold uppercase tracking-wider " +
                        (isHighlight ? "text-brand-700" : "text-ink-600")
                      }
                    >
                      {name}
                      {isHighlight ? (
                        <span className="ml-1.5 inline-block rounded-full bg-brand-600 px-1.5 py-0.5 align-middle text-[9px] font-bold text-white">
                          ★
                        </span>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr
                  key={row.label}
                  className="border-t border-ink-200/60 transition-colors hover:bg-brand-50/30"
                >
                  <td className="px-5 py-3.5 text-sm font-medium text-ink-800">
                    {row.label}
                  </td>
                  {row.values.map((v, i) => (
                    <td
                      key={i}
                      className={
                        "px-5 py-3.5 text-center " +
                        (i === 1 ? "bg-brand-50/40" : "")
                      }
                    >
                      {renderCell(v, i === 1)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- FAQ ------------------------------------- */

function PricingFaqSection() {
  return (
    <section className="px-6 py-14 lg:px-12 lg:py-20">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700">
            Sıkça Sorulan Sorular
          </div>
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-ink-900 sm:text-4xl">
            Fiyatlandırma hakkında merak edilenler
          </h2>
          <p className="mt-3 text-base leading-relaxed text-ink-600">
            Cevabını bulamadığınız bir sorunuz varsa{" "}
            <Link
              to="/contact"
              className="font-medium text-brand-700 underline-offset-4 hover:underline"
            >
              bizimle iletişime geçin
            </Link>
            .
          </p>
        </div>

        <div className="mt-10 space-y-3">
          {pricingFaqs.map((item) => (
            <details
              key={item.q}
              className="group rounded-2xl border border-ink-200/70 bg-white p-5 transition-all open:shadow-md open:shadow-ink-900/5 hover:border-brand-200"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                <h3 className="text-base font-semibold text-ink-900 sm:text-lg">
                  {item.q}
                </h3>
                <ChevronDown className="h-5 w-5 shrink-0 text-ink-400 transition-transform duration-200 group-open:rotate-180 group-open:text-brand-600" />
              </summary>
              <p className="mt-4 text-sm leading-relaxed text-ink-600 sm:text-base">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- CTA ------------------------------------ */

function PricingCta({ isAuthed }: { isAuthed: boolean }) {
  return (
    <section className="px-6 py-20 lg:px-12 lg:py-28">
      <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-ink-200/70 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 p-10 text-center shadow-2xl shadow-ink-900/20 sm:p-16">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-32 -bottom-32 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]"
        />

        <div className="relative">
          <h2 className="text-balance text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl">
            Atölyenizi 14 gün ücretsiz deneyin
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-balance text-base leading-relaxed text-ink-300">
            Kredi kartı istemiyoruz. Beğenmezseniz ödemezsiniz. Kararınızı
            verirken tüm özellikleri kullanabilirsiniz.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {isAuthed ? (
              <Button asChild size="lg" className="gap-2">
                <Link to="/dashboard">
                  Uygulamaya Git
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild size="lg" className="gap-2">
                  <Link to="/register">
                    Ücretsiz hesap oluştur
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/contact">Bize ulaş</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ JSON-LD ------------------------------------ */

function PricingJsonLd() {
  const productData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Ustorio",
    description:
      "Atölye yönetim SaaS. Müşteri, araç, servis kaydı, parça tedariki ve mali durumu tek panelden yönetin.",
    brand: { "@type": "Brand", name: "Ustorio" },
    offers: pricingTiers.map((tier) => ({
      "@type": "Offer",
      name: tier.name,
      description: tier.tagline,
      priceCurrency: "TRY",
      price:
        tier.price === "Teklife Bağlı"
          ? undefined
          : tier.price.replace(/[^\d]/g, ""),
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        billingIncrement: 1,
        unitCode: "MON",
      },
      availability: "https://schema.org/InStock",
    })),
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pricingFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqData) }}
      />
    </>
  );
}
